import { DefaultOptionType } from 'antd/es/select';
import { CountryCode, getCountries, getCountryCallingCode, parsePhoneNumber } from 'libphonenumber-js';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { Form, Input, Select, Space, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import styles from './phone-input.module.css';
import * as Flags from 'country-flag-icons/react/3x2';

interface Props {
  value: string;
  inputLabel?: string;
  onChange?: (value: string) => void;
  countryCode?: string;
}

const getPrefix = (selectedValue: string) => {
  return selectedValue.split('_')[0];
};

const getCountryName = (selectedValue: string) => {
  return selectedValue.split('_')[1];
};

const PhoneInput = ({ onChange, inputLabel, value, countryCode = 'LT', ...restFormProps }: Props): JSX.Element => {
  const [number, setNumber] = useState('');
  const { i18n } = useTranslation();

  const [prefixSelectValue, setPrefixSelectValue] = useState('');
  const [initialValueLoaded, setInitialValueLoaded] = useState(false);

  const regionNamesInEnglish = useMemo(
    () => new Intl.DisplayNames([i18n.language], { type: 'region' }),
    [i18n.language],
  );
  const countriesList = useMemo(() => getCountries(), []);

  const phoneNumberChange = (prefix: string, phoneNumber: string) =>
    onChange?.(phoneNumber.length ? `${prefix}${phoneNumber}` : '');

  const handlePrefixChange = (namePrefix: string) => {
    const prefix = getPrefix(namePrefix);
    setPrefixSelectValue(namePrefix);
    phoneNumberChange(prefix, number);
  };

  const handleNumberChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value: inputValue } = event.target;
    const numberRegex = /^[0-9]*$/;

    if (numberRegex.test(inputValue) || inputValue === '') {
      const prefix = getPrefix(prefixSelectValue);
      setNumber(inputValue || '');
      phoneNumberChange(prefix, inputValue);
    }
  };

  const filterCountries = (inputValue: string, option: DefaultOptionType | undefined) => {
    if (typeof option?.value === 'string') {
      const prefix = getPrefix(option.value);
      const countryName = getCountryName(option.value);

      return (
        prefix.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0 ||
        countryName.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
      );
    }
    return false;
  };

  useEffect(() => {
    if (!initialValueLoaded) {
      if (value?.length) {
        const phoneNumber = parsePhoneNumber(value);
        const countryName = regionNamesInEnglish.of(phoneNumber.country || 'US');

        setNumber(phoneNumber.nationalNumber);
        setPrefixSelectValue(`+${phoneNumber.countryCallingCode}_${countryName}`);
      } else {
        const countryPrefix = getCountryCallingCode(countryCode as CountryCode);

        setPrefixSelectValue(`+${countryPrefix}_${regionNamesInEnglish.of(countryCode)}`);
      }
      setInitialValueLoaded(true);
    }
  }, [value, countriesList, initialValueLoaded, regionNamesInEnglish, countryCode]);

  return (
    <div data-testid="phoneInputId">
      <Space.Compact className={styles.container}>
        <Form.Item noStyle className={styles.selectFormItem}>
          <Select
            data-testid="phoneInputSelectId"
            showSearch
            className={styles.select}
            value={prefixSelectValue}
            filterOption={filterCountries}
            optionLabelProp="label"
            popupMatchSelectWidth={false}
            onChange={handlePrefixChange}
          >
            {countriesList.map(country => {
              const Flag = Flags[country];
              const selectValue = `+${getCountryCallingCode(country)}_${regionNamesInEnglish.of(country)}`;

              return (
                <Select.Option
                  key={selectValue}
                  value={selectValue}
                  label={
                    <Space>
                      <Flag className={styles.flag} />
                      <Typography.Text>
                        <span>+</span>
                        {getCountryCallingCode(country)}
                      </Typography.Text>
                    </Space>
                  }
                >
                  <Space>
                    <Flag className={styles.flag} />
                    <Typography.Text>{regionNamesInEnglish.of(country)}</Typography.Text>
                    <Typography.Text>
                      <span>
                        <span>+</span>
                        {getCountryCallingCode(country)}
                      </span>
                    </Typography.Text>
                  </Space>
                </Select.Option>
              );
            })}
          </Select>
        </Form.Item>
        <Form.Item noStyle>
          <Input {...restFormProps} placeholder={inputLabel} value={number} onChange={handleNumberChange} />
        </Form.Item>
      </Space.Compact>
    </div>
  );
};

export default PhoneInput;
